/** Grid and timing configuration for Rush Fever 7s Deluxe */

export const GameConfig = {
  /** Number of reels visually displayed in main grid */
  VISUAL_REEL_COUNT: 5,
  /** Number of reels in base game (includes extra reel for math evaluation) */
  BASE_REEL_COUNT: 6,
  /** Number of reels in free game */
  FREE_GAME_REEL_COUNT: 5,
  /** Number of visible rows per reel */
  VISIBLE_ROWS: 3,
  /** Number of ways (3^5 = 243 for 5 reels) */
  WAYS: 243,

  /** Timing constants (milliseconds) — tuned for real casino feel */
  REEL_SPIN_UP_TIME: 100,       // quick acceleration
  REEL_SPIN_DURATION: 400,      // short spin before stopping begins
  REEL_STOP_DELAY: 240,         // between reel stops
  REEL_BOUNCE_DURATION: 70,     // very short subtle bounce on stop
  REEL_BOUNCE_OVERSHOOT_PX: 4,  // subtle overshoot
  WIN_DWELL_TIME: 2000,
  SPIN_MIN_DURATION: 500,       // first reel stops after 0.5s
  TURBO_REEL_STOP_DELAY: 50,    // turbo: near-instant cascade
  TURBO_SPIN_MIN_DURATION: 200,  // turbo: 0.2s before first stop

  /** Spin animation speeds — fast scroll for casino feel */
  SPIN_SPEED: 60,               // fast base spin speed
  SPIN_MAX_SPEED: 90,           // high top speed
  SPIN_DECEL_RATE: 5,           // quick deceleration snap to stop

  /** Bet levels */
  BET_LEVELS: [0.01, 0.02, 0.05, 0.10, 0.15, 0.20, 0.30, 0.40, 1.00] as readonly number[],
  /** Wager multiplier (total bet = bet level * multiplier) */
  WAGER_MULTIPLIER: 50,
  /** Starting balance */
  STARTING_BALANCE: 1000.00,

  /** Big win thresholds (multiples of total bet) */
  BIG_WIN_AMAZING: 10,
  BIG_WIN_AWESOME: 25,
  BIG_WIN_INCREDIBLE: 50,

  /** Design resolution */
  DESIGN_WIDTH_LANDSCAPE: 1920,
  DESIGN_HEIGHT_LANDSCAPE: 1080,
  DESIGN_WIDTH_PORTRAIT: 1080,
  DESIGN_HEIGHT_PORTRAIT: 1920,

  /** Symbol display dimensions */
  SYMBOL_WIDTH: 180,
  SYMBOL_HEIGHT: 160,

  /** Reel spacing */
  REEL_GAP: 8,

  /** Buffer symbols above and below visible area for smooth scrolling */
  BUFFER_SYMBOLS: 2,
} as const;

/** Symbol IDs used throughout the game */
export const SymbolId = {
  WILD: 'WILD',
  PIC_A: 'PICa',
  PIC_B: 'PICb',
  PIC_C: 'PICc',
  PIC_D: 'PICd',
  PIC_E: 'PICe',
  ACE: 'A',
  KING: 'K',
  QUEEN: 'Q',
  JACK: 'J',
  TEN: 'T',
  SCATTER: 'SCATTER',
  RUSH: 'Rush',
} as const;

/** Map from symbol ID to asset path (relative to public/) */
export const SYMBOL_TEXTURE_MAP: Record<string, string> = {
  [SymbolId.WILD]: 'assets/symbols/wild.png',
  [SymbolId.PIC_A]: 'assets/symbols/pic_a.png',
  [SymbolId.PIC_B]: 'assets/symbols/pic_b.png',
  [SymbolId.PIC_C]: 'assets/symbols/pic_c.png',
  [SymbolId.PIC_D]: 'assets/symbols/pic_d.png',
  [SymbolId.PIC_E]: 'assets/symbols/pic_e.png',
  [SymbolId.SCATTER]: 'assets/symbols/scatter.png',
  [SymbolId.RUSH]: 'assets/symbols/diamond.png',
  // Royals - fruit-themed card images
  [SymbolId.ACE]: 'assets/sprites/card_a.png',
  [SymbolId.KING]: 'assets/sprites/card_b.png',
  [SymbolId.QUEEN]: 'assets/sprites/card_c.png',
  [SymbolId.JACK]: 'assets/sprites/card_d.png',
  [SymbolId.TEN]: 'assets/sprites/card_e.png',
};

/** Colors for royal placeholder rectangles */
export const ROYAL_COLORS: Record<string, number> = {
  [SymbolId.ACE]: 0x22cc44,   // Green (watermelon)
  [SymbolId.KING]: 0x8833aa,  // Purple (grapes)
  [SymbolId.QUEEN]: 0x6633bb, // Plum
  [SymbolId.JACK]: 0xeedd33,  // Yellow (lemon)
  [SymbolId.TEN]: 0xdd2233,   // Red (cherry)
};

/** Royal label text */
export const ROYAL_LABELS: Record<string, string> = {
  [SymbolId.ACE]: 'A',
  [SymbolId.KING]: 'K',
  [SymbolId.QUEEN]: 'Q',
  [SymbolId.JACK]: 'J',
  [SymbolId.TEN]: '10',
};
